"use client";

import "@/polyfills";
import { GoogleLoginModal } from "@/components/auth/GoogleLoginModal";
import { FacebookPixel } from "@/components/facebook-pixel";
import { VersionUpdatePopup } from "@/components/home/version-update-popup/version-update-popup";
import { LoginExpiredDialog } from "@/components/login-expired-dialog";
import NavigationProgress from "@/components/navigation-progress";
import { PullToRefreshIcon } from "@/components/pull-to-refresh-icon";
import { Toaster } from "@/components/ui/sonner";
import { LoginModalProvider } from "@/contexts/login-modal-context";
import { ViewportHeightProvider } from "@/contexts/ViewportHeightContext";
import { initEventCore } from "@/event-core/init";
import { registerAll } from "@/events/registerAll";
import { ThemeProvider } from "@/features/theme/theme-provider";
import { useIsKeyboardOpen } from "@/hooks/use-keyboard-offset";
import { useNotification } from "@/hooks/use-notification";
import { usePushTokenUpdate } from "@/hooks/use-push-token-update";
import { useSocketInit } from "@/hooks/use-socket-init";
import { useAppVersionCheck } from "@/hooks/useAppVersionCheck";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useAuthStore } from "@/store/auth";
import { useShareModal } from "@/store/use-share-modal";
import { useUserInfo } from "@/store/user-info";
import { skipRoutes } from "@/utils/router";
import { clearSearchState } from "@/utils/search-state";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// const ImDrawer = dynamic( () => import( "@/components/im/im-drawer" ).then( m => m.default ), { ssr: false } );
const Freshdesk = dynamic( () => import( "@/components/Freshdesk" ).then( m => m.default ), { ssr: false } );

const ShareModal = dynamic( () => import( "@/components/position/shareModal" ).then( m => m.ShareModal ), {
    ssr: false,
} );
const SharePannel = dynamic( () => import( "@/components/share/sharePannel" ).then( m => m.SharePannel ), {
    ssr: false,
} );
const TradePanel = dynamic( () => import( "@/components/trade-panel/trade-panel" ).then( m => m.TradePanel ), {
    ssr: false,
} );
const MarketOrderFilledDialog = dynamic(
    () => import( "@/components/market-order-filled-dialog" ).then( m => m.MarketOrderFilledDialog ),
    { ssr: false },
);

const DevToolsOverlay = process.env.NODE_ENV === "development"
    ? dynamic( () => import( "@/components/dev/dev-tools-overlay" ).then( m => m.DevToolsOverlay ), { ssr: false } )
    : () => null;

initEventCore( registerAll );
function BodyKeyboardAttrSync() {
    const { isKeyboardOpen } = useIsKeyboardOpen();
    useEffect( () => {
        if ( isKeyboardOpen ) {
            document.body.setAttribute( "data-keyboard-open", "" );
        } else {
            document.body.removeAttribute( "data-keyboard-open" );
        }
        return () => document.body.removeAttribute( "data-keyboard-open" );
    }, [ isKeyboardOpen ] );
    return null;
}

const getChannelFromUrl = () => {
    if ( typeof window === "undefined" ) return;
    const url = new URL( window.location.href );
    window.channel = url.searchParams.get( "channel" ) || "";
    if ( window.channel ) {
        localStorage.setItem( "channel", window.channel );
    } else {
        window.channel = localStorage.getItem( "channel" ) || "";
    }
};
getChannelFromUrl();

const savePixelIdFromUrl = () => {
    if ( typeof window === "undefined" ) return;
    const url = new URL( window.location.href );
    const pixelId = url.searchParams.get( "pixelid" );
    if ( pixelId ) {
        localStorage.setItem( "fb_pixel_id", pixelId );
    }
};
savePixelIdFromUrl();

export function RootLayoutClient( { children }: { children: React.ReactNode } ) {
    return (
        <ThemeProvider>
            <LoginModalProvider>
                <RootLayoutClientInner>{children}</RootLayoutClientInner>
            </LoginModalProvider>
            <ToasterPortal />
        </ThemeProvider>
    );
}

function ToasterPortal() {
    const [ mounted, setMounted ] = useState( false );
    useEffect( () => {
        setMounted( true );
    }, [] );

    if ( !mounted ) return null;
    return createPortal( <Toaster />, document.body );
}

function RootLayoutClientInner( { children }: { children: React.ReactNode } ) {
    const pathname = usePathname();
    // 创建 QueryClient 实例 - 全局共享
    const [ queryClient ] = useState( () =>
        new QueryClient( {
            defaultOptions: {
                queries: { staleTime: 5 * 1000, gcTime: 10 * 1000, retry: 3, refetchOnWindowFocus: false },
            },
        } )
    );

    // 路由切换时暂时隐藏 overflow-y，200ms 后移除（用于避免切换时的滚动闪动等）
    const [ overflowYHidden, setOverflowYHidden ] = useState( false );
    useEffect( () => {
        setOverflowYHidden( true );
        const t = setTimeout( () => setOverflowYHidden( false ), 400 );
        return () => clearTimeout( t );
    }, [ pathname ] );

    usePushTokenUpdate();
    const { showVersionPopup, versionInfo, handleVersionPopupOpenChange } = useAppVersionCheck();

    useNotification();

    const { isOpenSharePanel, closeSharePanel, clearSharePanel, shareUrl, shareTitle, shareText, shareImage } =
        useShareModal();
    const token = useAuthStore( ( state ) => state.token );
    const { fetchUserInfo } = useUserInfo();
    const prevPathnameRef = useRef<string | null>( null );

    useEffect( () => {
        if ( !token || skipRoutes.includes( pathname ) ) return;
        fetchUserInfo( `root-layout:auth-or-pathname:${pathname}` );
    }, [ fetchUserInfo, pathname, token ] );

    // 监听路由变化，处理搜索页状态清除
    useEffect( () => {
        const prevPathname = prevPathnameRef.current;
        prevPathnameRef.current = pathname;

        // 如果从搜索页跳转到非详情页，清除保存的搜索状态
        if ( prevPathname === "/search" && pathname !== "/search" ) {
            // 详情页路径列表
            const eventDetailPaths = [ "/event-detail", "/sport-event-detail", "/main-event-detail" ];

            // 如果不是跳转到详情页，清除搜索状态
            const isEventDetailPage = eventDetailPaths.some( ( detailPath ) => pathname.startsWith( detailPath ) );

            if ( !isEventDetailPage ) {
                clearSearchState();
            }
        }
    }, [ pathname ] );

    // 定义 handleRefresh，在使用前必须先定义
    const handleRefresh = async () => {
        const start = Date.now();
        try {
            // invalidateQueries 会自动触发活动查询的重新获取，无需再调用 refetchQueries
            await queryClient.invalidateQueries();
            if ( !skipRoutes.includes( pathname ) ) {
                await fetchUserInfo( `root-layout:pull-refresh:${pathname}` );
            }
        } finally {
            const elapsed = Date.now() - start;
            const MIN_REFRESH_MS = 500;
            if ( elapsed < MIN_REFRESH_MS ) {
                await new Promise( r => setTimeout( r, MIN_REFRESH_MS - elapsed ) );
            }
        }
    };

    const { distance, isRefreshing: isPulling, contentOffsetY } = usePullToRefresh( {
        onRefresh: handleRefresh,
        threshold: 80,
        maxDistance: 20,
    } );

    // 计算刷新指示器显示进度和状态
    const threshold = 80;
    const refreshProgress = Math.min( distance / threshold, 1 );
    const isReady = distance >= threshold;
    // 最小不透明度 0.6（60%），到位时 1.0（100%）
    // 未到位时：0.6 + (refreshProgress * 0.4)，范围 [0.6, 1.0)
    // 到位时：1.0
    const refreshOpacity = isReady ? 1 : Math.max( 0.6, 0.6 + ( refreshProgress * 0.4 ) );

    // 添加最小下拉距离阈值，避免过早渲染刷新指示器
    const MIN_PROGRESS_THRESHOLD = 0.1; // 进度小于 10% 时不渲染
    const shouldShowIndicator = refreshProgress >= MIN_PROGRESS_THRESHOLD;

    // 阻止 Safari 浏览器缩放
    useEffect( () => {
        // 阻止双指缩放
        const preventZoom = ( e: TouchEvent ) => {
            if ( e.touches.length > 1 ) {
                e.preventDefault();
            }
        };

        // 阻止双击缩放
        let lastTouchEnd = 0;
        const preventDoubleTapZoom = ( e: TouchEvent ) => {
            const now = Date.now();
            if ( now - lastTouchEnd <= 300 ) {
                e.preventDefault();
            }
            lastTouchEnd = now;
        };

        document.addEventListener( "touchstart", preventZoom, { passive: false } );
        document.addEventListener( "touchend", preventDoubleTapZoom, { passive: false } );

        return () => {
            document.removeEventListener( "touchstart", preventZoom );
            document.removeEventListener( "touchend", preventDoubleTapZoom );
        };
    }, [] );

    // 禁用右键菜单
    useEffect( () => {
        const preventContextMenu = ( e: MouseEvent ) => {
            e.preventDefault();
        };

        document.body.addEventListener( "contextmenu", preventContextMenu );

        return () => {
            document.body.removeEventListener( "contextmenu", preventContextMenu );
        };
    }, [] );
    useSocketInit();

    return (
        <QueryClientProvider client={queryClient}>
            <ViewportHeightProvider>
                <FacebookPixel />
                <BodyKeyboardAttrSync />
                {/* 全局导航进度条 - 所有页面共享 */}
                <Suspense fallback={null}>
                    <NavigationProgress />
                </Suspense>

                {/* 固定在头部的刷新指示器 */}
                {shouldShowIndicator && refreshOpacity > 0 && (
                    <div
                        className="fixed top-0 left-0 right-0 flex justify-center items-center"
                        style={{
                            zIndex: 9999,
                            paddingTop: "0",
                            transform: `translateY(${distance - 20}px)`,
                            transition: isPulling ? "none" : "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                            opacity: refreshOpacity,
                        }}
                    >
                        <PullToRefreshIcon progress={refreshProgress} isReady={isReady} isRefreshing={isPulling} />
                    </div>
                )}
                <div
                    className={`h-[calc(var(--vh,1vh)*100)] relative overflow-x-hidden ${
                        overflowYHidden ? "overflow-y-hidden" : ""
                    }`}
                    id="app-page"
                    style={{ "--pull-offset": `${contentOffsetY}px` } as React.CSSProperties}
                >
                    <Suspense fallback={null}>{children}</Suspense>
                </div>
                {/* 新版交易面板 */}
                <Suspense fallback={null}>
                    <TradePanel />
                </Suspense>
                <Suspense fallback={null}>
                    <MarketOrderFilledDialog />
                </Suspense>

                {/* 登录过期提示 - 所有页面共享 */}
                <LoginExpiredDialog />
                <Suspense fallback={null}>
                    <GoogleLoginModal />
                </Suspense>

                {/* 版本更新弹窗 - 全局共享 */}
                <VersionUpdatePopup
                    open={showVersionPopup}
                    versionInfo={versionInfo}
                    onOpenChange={handleVersionPopupOpenChange}
                />

                {/* 开发工具 overlay（仅 development） */}
                {/* <DevToolsOverlay /> */}
                {/* <ImDrawer /> */}
                <Freshdesk />
                <ShareModal />
                <SharePannel
                    open={isOpenSharePanel}
                    onOpenChange={( open ) => ( open ? null : closeSharePanel() )}
                    shareUrl={shareUrl}
                    shareImage={shareImage}
                    shareTitle={shareTitle}
                    shareText={shareText}
                    onClosed={clearSharePanel}
                />
            </ViewportHeightProvider>
        </QueryClientProvider>
    );
}
